/**
 * Templates → Sheet Notes: the firm's per-sheet-type notes (docs/bim-plan/28 round 10). AlderCAD
 * prints a sheet type's blocks in the title strip's notes band, numbered, in this order. Each
 * block: a heading, its notes (one per line), when it applies (always, or only when the model
 * has a pool, is in Newport Beach, ...), and whether it prints. Saves on leaving a box; changes
 * show the next time a model is opened.
 */
import { useEffect, useMemo, useState } from 'react'
import { listSheetNotes, saveSheetNote, addSheetNote, removeSheetNote, moveSheetNote, SHEET_NOTE_TYPES } from '../services/sheetNotes.js'
import { NOTE_FACTS } from '../bim/sheets/sheetNotesBand.js'
import { confirmDialog } from './Dialog.jsx'

const FONT = 'DM Sans, sans-serif'
const input = { fontFamily: FONT, fontSize: 12, padding: '5px 7px', borderRadius: 4, border: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', boxSizing: 'border-box' }
const smallBtn = { fontFamily: FONT, fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer' }

/** "pool,!renovation" ↔ the checkbox list */
function AppliesPicker({ value, onChange, disabled }) {
  const keys = new Set(String(value || '').split(/[,\s]+/).filter(Boolean))
  const toggle = (k) => {
    const next = new Set(keys)
    if (next.has(k)) { next.delete(k); next.add('!' + k) } else if (next.has('!' + k)) next.delete('!' + k); else next.add(k)
    onChange([...next].join(','))
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {Object.entries(NOTE_FACTS).map(([k, label]) => {
        const on = keys.has(k), not = keys.has('!' + k)
        return (
          <button key={k} type="button" disabled={disabled} onClick={() => toggle(k)} title={label + ' (click: only when / only when NOT / either)'}
            style={{ ...smallBtn, fontSize: 10.5, padding: '2px 7px', background: on ? '#07272D' : not ? 'rgba(184,100,10,0.15)' : 'transparent', color: on ? '#fff' : not ? '#b8640a' : 'var(--aj-text-muted)', borderColor: on ? '#07272D' : not ? '#e0b98a' : 'var(--aj-border)' }}>
            {not ? 'not ' : ''}{label.replace(/^Model has |^Project is |^Building has /, '')}
          </button>
        )
      })}
    </div>
  )
}

function Block({ b, first, last, onChanged, onRemoved, onMoved, addToast }) {
  const [title, setTitle] = useState(b.title)
  const [body, setBody] = useState(b.body)
  const [busy, setBusy] = useState(false)
  useEffect(() => { setTitle(b.title); setBody(b.body) }, [b.title, b.body])
  async function commit(patch) {
    setBusy(true)
    try { onChanged(await saveSheetNote(b.id, patch)) } catch (e) { addToast('Save failed: ' + (e.message || 'unknown error')); setTitle(b.title); setBody(b.body) } finally { setBusy(false) }
  }
  async function remove() {
    if (!(await confirmDialog({ title: 'Remove notes block?', message: `Remove "${b.title}" and its notes from this sheet type?`, confirmLabel: 'Remove', danger: true }))) return
    try { await removeSheetNote(b.id); onRemoved(b.id) } catch (e) { addToast('Remove failed: ' + (e.message || 'unknown error')) }
  }
  const count = String(body || '').split('\n').filter((l) => l.trim()).length
  return (
    <div style={{ border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 12, marginBottom: 12, background: 'var(--aj-bg-surface)', opacity: b.active ? 1 : 0.55 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <input value={title} disabled={busy} onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if (title.trim() !== b.title) commit({ title: title.trim() }) }}
          placeholder="Heading" style={{ ...input, flex: 1, fontWeight: 600, textTransform: 'uppercase' }} />
        <label style={{ fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-mid)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={b.active} disabled={busy} onChange={(e) => commit({ active: e.target.checked })} /> Prints
        </label>
        <button style={smallBtn} disabled={first || busy} onClick={() => onMoved(b.id, -1)} title="Move up">↑</button>
        <button style={smallBtn} disabled={last || busy} onClick={() => onMoved(b.id, 1)} title="Move down">↓</button>
        <button style={{ ...smallBtn, border: 'none' }} onClick={remove} title="Remove">×</button>
      </div>
      <textarea value={body} disabled={busy} rows={Math.min(14, Math.max(3, count + 1))} onChange={(e) => setBody(e.target.value)}
        onBlur={() => { if (body !== b.body) commit({ body }) }}
        placeholder="One note per line. They print numbered, in capitals."
        style={{ ...input, width: '100%', resize: 'vertical', lineHeight: 1.45 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 8 }}>
        <span style={{ fontFamily: FONT, fontSize: 11, color: 'var(--aj-text-muted)', paddingTop: 3, whiteSpace: 'nowrap' }}>Prints when</span>
        <div style={{ flex: 1 }}>
          <AppliesPicker value={b.appliesWhen} disabled={busy} onChange={(v) => commit({ appliesWhen: v })} />
          <div style={{ fontFamily: FONT, fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 4 }}>
            {b.appliesWhen ? 'Only when every highlighted condition holds for the model.' : 'Always (nothing highlighted).'} {count} note{count === 1 ? '' : 's'}.
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SheetNotesList({ addToast }) {
  const [rows, setRows] = useState(null)
  const [type, setType] = useState(SHEET_NOTE_TYPES[5].id)   // A10X Floor Plans
  const reload = () => listSheetNotes().then(setRows).catch((e) => { addToast('Could not load sheet notes: ' + e.message); setRows([]) })
  useEffect(() => { reload() }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => {
    const m = {}
    for (const r of rows || []) m[r.templateId] = (m[r.templateId] || 0) + 1
    return m
  }, [rows])
  const mine = useMemo(() => (rows || []).filter((r) => r.templateId === type).sort((a, b) => a.sortOrder - b.sortOrder), [rows, type])

  async function add() {
    try { const row = await addSheetNote(type, 'NOTES'); setRows((r) => [...(r || []), row]) } catch (e) { addToast('Add failed: ' + (e.message || 'unknown error')) }
  }
  async function move(id, dir) {
    try { await moveSheetNote(id, dir); await reload() } catch (e) { addToast('Move failed: ' + (e.message || 'unknown error')) }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', gap: 16, padding: '0 24px 24px' }}>
      <div style={{ width: 230, flexShrink: 0, overflowY: 'auto' }}>
        {SHEET_NOTE_TYPES.map((t) => (
          <div key={t.id} onClick={() => setType(t.id)}
            style={{ padding: '7px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, fontFamily: FONT, fontSize: 12.5, color: 'var(--aj-text)', display: 'flex', gap: 8, background: type === t.id ? 'var(--aj-bg-subtle)' : 'transparent', border: type === t.id ? '0.5px solid var(--aj-border-mid)' : '0.5px solid transparent' }}>
            <span style={{ flex: 1 }}>{t.name}</span>
            {counts[t.id] ? <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>{counts[t.id]}</span> : null}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)', margin: '2px 0 12px', maxWidth: 820, lineHeight: 1.5 }}>
          The notes AlderCAD prints in this sheet type's title strip, in this order. A block can be limited to models where it applies (a pool, a remodel, Newport Beach). Changes show the next time a model is opened.
        </div>
        {rows === null ? (
          <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading…</div>
        ) : (
          <div style={{ maxWidth: 820 }}>
            {!mine.length && <div style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text-muted)', marginBottom: 12 }}>No notes for this sheet type yet.</div>}
            {mine.map((b, i) => (
              <Block key={b.id} b={b} first={i === 0} last={i === mine.length - 1} addToast={addToast}
                onChanged={(row) => setRows((r) => r.map((x) => (x.id === row.id ? row : x)))}
                onRemoved={(id) => setRows((r) => r.filter((x) => x.id !== id))}
                onMoved={move} />
            ))}
            <button onClick={add} style={{ fontFamily: FONT, fontSize: 12, padding: '6px 14px', borderRadius: 20, border: '0.5px solid #07272D', background: '#07272D', color: '#fff', cursor: 'pointer' }}>
              + Add notes block
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
